// Pages bailleur : liste des baux, création, fiche du bail (onglets), tableau de bord, onglet de candidature.
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { post } from "@core/api";
import { money, date, dateTime } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { extensions } from "@core/modules";
import { useOrg, useRestricted } from "@core/org";
import {
  Button,
  ButtonLink,
  Card,
  Empty,
  ErrorBox,
  Input,
  KeyValues,
  Loading,
  MoneyInput,
  Notice,
  PageHeader,
  Select,
  Source,
  Table,
  Tabs,
  fieldError,
} from "@core/ui";
import { LEGAL_NOTICE, LeaseStatus, REGIMES, useAction, type LeaseDetail, type Prorata } from "./shared";
import { DepositTab, HandbookTab, HandoverTab, InspectionsTab, SignatureTab, TermsTab } from "./tabs";

type LeaseRow = {
  id: string;
  status: string;
  status_label: string;
  regime_label: string;
  start_on: string;
  end_on: string | null;
  rent_cents: number;
  charges_cents: number;
  property_label: string;
  tenants: string;
  pending_signers: number;
};

const STATUS_FILTERS = [
  { value: "", label: "Tous les baux" },
  { value: "brouillon", label: "Brouillons" },
  { value: "en_signature", label: "En signature" },
  { value: "signe", label: "Signés, à activer" },
  { value: "actif", label: "Locations actives" },
  { value: "termine", label: "Terminés" },
];

export function LeasesPage() {
  const org = useOrg();
  const [status, setStatus] = useState("");
  const q = useGet<{ items: LeaseRow[] }>(org.api(`/leases${status ? `?status=${status}` : ""}`));
  return (
    <div className="page">
      <PageHeader
        eyebrow="Gestion"
        title="Baux"
        intro="Du bail préparé à la remise des clés : versions, signatures, états des lieux et dépôt de garantie."
        actions={org.can("operate") ? <ButtonLink to={org.to("baux/nouveau")}>Préparer un bail</ButtonLink> : null}
      />
      <div className="row">
        <Select label="Afficher" value={status} options={STATUS_FILTERS} onChange={(e) => setStatus(e.target.value)} />
      </div>
      <Card>
        {q.isLoading ? <Loading /> : null}
        <ErrorBox error={q.error} />
        {q.data && q.data.items.length === 0 ? (
          <Empty title="Aucun bail pour le moment">Préparez un bail depuis une candidature retenue ou saisissez-le directement.</Empty>
        ) : null}
        {q.data && q.data.items.length > 0 ? (
          <Table caption="Baux de l’espace">
            <thead>
              <tr>
                <th>Logement</th>
                <th>Locataires</th>
                <th>Statut</th>
                <th>Prise d’effet</th>
                <th className="right">Loyer + charges</th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((l) => (
                <tr key={l.id}>
                  <td>
                    <Link to={org.to(`baux/${l.id}`)}>{l.property_label}</Link>
                    <span className="block muted small">{l.regime_label}</span>
                  </td>
                  <td>{l.tenants || <span className="muted">À renseigner</span>}</td>
                  <td>
                    <LeaseStatus status={l.status} />
                    {l.pending_signers > 0 ? <span className="block muted small">{l.pending_signers} signature(s) attendue(s)</span> : null}
                  </td>
                  <td>{date(l.start_on)}</td>
                  <td className="right entree-money">{money(l.rent_cents + l.charges_cents)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Création manuelle

type PropertyOption = { id: string; label: string; kind: string; furnished: boolean; address: string; occupied: boolean };
type RegimeOption = { value: string; label: string; deposit_cap_months: number | null; default_duration_months: number };
type PartyForm = { role: string; full_name: string; email: string; phone: string };

export function NewLeasePage() {
  const org = useOrg();
  const nav = useNavigate();
  const invalidate = useInvalidate();
  const opts = useGet<{ items: PropertyOption[]; regimes: RegimeOption[] }>(org.api("/leases/property-options"));
  const [form, setForm] = useState({ property_id: "", regime: "vide", start_on: "", duration_months: "", charges_mode: "provision", payment_day: "1" });
  const [rent, setRent] = useState<number | null>(null);
  const [charges, setCharges] = useState<number | null>(0);
  const [deposit, setDeposit] = useState<number | null>(0);
  const [parties, setParties] = useState<PartyForm[]>([{ role: "locataire", full_name: "", email: "", phone: "" }]);
  const { busy, error, run } = useAction();
  const regime = opts.data?.regimes.find((r) => r.value === form.regime);
  const capMonths = form.regime === "colocation" ? (opts.data?.items.find((p) => p.id === form.property_id)?.furnished ? 2 : 1) : regime?.deposit_cap_months;
  if (!org.can("operate")) return <Notice tone="attention">Votre rôle ne permet pas de préparer un bail.</Notice>;
  const setParty = (i: number, patch: Partial<PartyForm>) => setParties(parties.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <div className="page">
      <PageHeader eyebrow="Baux" title="Préparer un bail" intro="Pour un dossier retenu, préférez « Préparer le bail » depuis la candidature : les données sont reprises avec leur source." />
      <Notice tone="attention">{LEGAL_NOTICE}</Notice>
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          let created: { lease: { id: string } } | null = null;
          const ok = await run(
            "create",
            async () => {
              created = await post<{ lease: { id: string } }>(org.api("/leases"), {
                property_id: form.property_id,
                regime: form.regime,
                start_on: form.start_on,
                duration_months: form.duration_months ? Number(form.duration_months) : null,
                rent_cents: rent ?? 0,
                charges_cents: charges ?? 0,
                charges_mode: form.charges_mode,
                deposit_cents: deposit ?? 0,
                payment_day: Number(form.payment_day) || 1,
                parties: parties.filter((p) => p.full_name.trim()).map((p) => ({ ...p, email: p.email || null, phone: p.phone || null })),
              });
            },
            "Bail préparé en brouillon.",
          );
          if (ok && created) {
            invalidate(org.api("/leases"));
            nav(org.to(`baux/${(created as { lease: { id: string } }).lease.id}`));
          }
        }}
      >
        <ErrorBox error={error} />
        <Card title="Logement et régime">
          {opts.isLoading ? <Loading /> : null}
          <div className="grid-2">
            <Select
              label="Logement"
              required
              value={form.property_id}
              error={fieldError(error, "property_id")}
              options={[{ value: "", label: "Choisir un logement" }, ...(opts.data?.items ?? []).map((p) => ({ value: p.id, label: `${p.label}${p.occupied ? " (bail en cours)" : ""}` }))]}
              onChange={(e) => setForm({ ...form, property_id: e.target.value })}
            />
            <Select
              label="Régime"
              value={form.regime}
              error={fieldError(error, "regime")}
              options={REGIMES}
              help="Les baux commerciaux et professionnels se préparent dans le module Professionnels."
              onChange={(e) => setForm({ ...form, regime: e.target.value })}
            />
            <Input label="Prise d’effet" type="date" required value={form.start_on} error={fieldError(error, "start_on")} onChange={(e) => setForm({ ...form, start_on: e.target.value })} />
            <Input
              label="Durée (mois)"
              type="number"
              min={1}
              value={form.duration_months}
              placeholder={regime ? String(regime.default_duration_months) : ""}
              help={regime ? `Par défaut : ${regime.default_duration_months} mois.` : undefined}
              error={fieldError(error, "duration_months")}
              onChange={(e) => setForm({ ...form, duration_months: e.target.value })}
            />
          </div>
        </Card>
        <Card title="Conditions financières">
          <div className="grid-2">
            <MoneyInput label="Loyer mensuel hors charges" required cents={rent} onChange={setRent} error={fieldError(error, "rent_cents")} />
            <MoneyInput label="Charges mensuelles" cents={charges} onChange={setCharges} error={fieldError(error, "charges_cents")} />
            <Select
              label="Nature des charges"
              value={form.charges_mode}
              options={[
                { value: "provision", label: "Provision avec régularisation" },
                { value: "forfait", label: "Forfait" },
              ]}
              onChange={(e) => setForm({ ...form, charges_mode: e.target.value })}
            />
            <MoneyInput
              label="Dépôt de garantie"
              cents={deposit}
              onChange={setDeposit}
              error={fieldError(error, "deposit_cents")}
              help={
                capMonths === 0
                  ? "Aucun dépôt ne peut être demandé pour un bail mobilité."
                  : capMonths && rent
                    ? `Plafond : ${capMonths} mois de loyer hors charges, soit ${money(capMonths * rent)}.`
                    : undefined
              }
            />
            <Input label="Jour de paiement" type="number" min={1} max={28} value={form.payment_day} error={fieldError(error, "payment_day")} onChange={(e) => setForm({ ...form, payment_day: e.target.value })} />
          </div>
        </Card>
        <Card title="Locataires et garants" actions={<Button variant="secondaire" small onClick={() => setParties([...parties, { role: "locataire", full_name: "", email: "", phone: "" }])}>Ajouter une personne</Button>}>
          <p className="muted small">Le bailleur est ajouté d’après le propriétaire rattaché au logement. Une adresse e-mail est nécessaire pour signer.</p>
          {parties.map((p, i) => (
            <div key={i} className="entree-grid-form">
              <Select
                label="Qualité"
                value={p.role}
                options={[
                  { value: "locataire", label: "Locataire" },
                  { value: "garant", label: "Garant" },
                ]}
                onChange={(e) => setParty(i, { role: e.target.value })}
              />
              <Input label="Nom et prénom" value={p.full_name} error={fieldError(error, `parties.${i}.full_name`)} onChange={(e) => setParty(i, { full_name: e.target.value })} />
              <Input label="Adresse e-mail" type="email" value={p.email} error={fieldError(error, `parties.${i}.email`)} onChange={(e) => setParty(i, { email: e.target.value })} />
              <Input label="Téléphone" type="tel" value={p.phone} onChange={(e) => setParty(i, { phone: e.target.value })} />
              {parties.length > 1 ? (
                <Button variant="discret" small onClick={() => setParties(parties.filter((_, j) => j !== i))}>
                  Retirer
                </Button>
              ) : null}
            </div>
          ))}
        </Card>
        <div className="form-actions">
          <Button type="submit" busy={busy === "create"}>
            Préparer le bail
          </Button>
          <ButtonLink to={org.to("baux")} variant="discret">
            Annuler
          </ButtonLink>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fiche du bail

type TabKey = string;

export function LeasePage() {
  const org = useOrg();
  const restricted = useRestricted();
  const { leaseId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const tab: TabKey = params.get("onglet") ?? "bail";
  const q = useGet<LeaseDetail>(org.api(`/leases/${leaseId}`));
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) {
    return (
      <div className="page">
        <ErrorBox error={q.error ?? new Error("Bail introuvable.")} />
        <Link to={org.to("baux")}>Revenir aux baux</Link>
      </div>
    );
  }
  const d = q.data;
  const ext = extensions("leaseTabs", { data: d, restricted });
  const items: { value: TabKey; label: string }[] = [
    { value: "bail", label: "Bail et annexes" },
    { value: "signature", label: "Signature" },
    { value: "edl", label: "État des lieux" },
    { value: "depot", label: "Dépôt" },
    { value: "livret", label: "Livret" },
    { value: "cles", label: "Clés" },
    ...ext.map((t) => ({ value: `x-${t.id}`, label: t.label })),
  ];
  const Ext = ext.find((t) => `x-${t.id}` === tab)?.Component;
  return (
    <div className="page">
      <PageHeader
        eyebrow={`Bail ${d.reference} · ${d.regime_label}`}
        title={d.property.label}
        intro={
          <>
            <LeaseStatus status={d.lease.status} /> <span className="muted">Prise d’effet le {date(d.lease.start_on)}{d.lease.end_on ? `, échéance le ${date(d.lease.end_on)}` : ""}</span>
          </>
        }
        actions={<ButtonLink to={org.to("baux")} variant="discret">Tous les baux</ButtonLink>}
      />
      <Tabs value={tab} items={items} onChange={(v) => setParams(v === "bail" ? {} : { onglet: v }, { replace: true })} />
      {tab === "bail" ? <TermsTab d={d} /> : null}
      {tab === "signature" ? <SignatureTab d={d} /> : null}
      {tab === "edl" ? <InspectionsTab d={d} /> : null}
      {tab === "depot" ? <DepositTab d={d} /> : null}
      {tab === "livret" ? <HandbookTab d={d} /> : null}
      {tab === "cles" ? <HandoverTab d={d} /> : null}
      {Ext ? <Ext orgId={org.orgId} leaseId={leaseId} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tableau de bord

type Summary = {
  a_signer: { lease_id: string; label: string; pending: number; total: number }[];
  signes_a_activer: { lease_id: string; label: string }[];
  brouillons: number;
  etats_des_lieux: { id: string; lease_id: string; label: string; kind: string; scheduled_at: string | null; status: string }[];
  depots_attendus: number;
};

export function Dashboard({ orgId }: { orgId: string }) {
  const q = useGet<Summary>(`/api/v1/orgs/${orgId}/leases/summary`);
  const to = (p: string) => `/espace/${orgId}/${p}`;
  const s = q.data;
  return (
    <Card title="Baux et entrées" actions={<Link to={to("baux")}>Voir les baux</Link>}>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {s ? (
        <div className="stack">
          {s.a_signer.length === 0 && s.signes_a_activer.length === 0 && s.etats_des_lieux.length === 0 && s.brouillons === 0 ? (
            <p className="muted">Aucun bail en préparation ni état des lieux prévu.</p>
          ) : null}
          {s.a_signer.length ? (
            <div>
              <p className="eyebrow">À signer</p>
              <ul className="list-plain">
                {s.a_signer.map((l) => (
                  <li key={l.lease_id}>
                    <Link to={to(`baux/${l.lease_id}?onglet=signature`)}>{l.label}</Link> <span className="muted small">· {l.pending} signature(s) attendue(s) sur {l.total}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {s.signes_a_activer.length ? (
            <div>
              <p className="eyebrow">Signés, location à activer</p>
              <ul className="list-plain">
                {s.signes_a_activer.map((l) => (
                  <li key={l.lease_id}>
                    <Link to={to(`baux/${l.lease_id}`)}>{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {s.etats_des_lieux.length ? (
            <div>
              <p className="eyebrow">États des lieux à venir</p>
              <ul className="list-plain">
                {s.etats_des_lieux.map((i) => (
                  <li key={i.id}>
                    <Link to={to(`baux/${i.lease_id}/etats-des-lieux/${i.id}`)}>
                      {i.kind === "sortie" ? "Sortie" : "Entrée"} · {i.label}
                    </Link>{" "}
                    <span className="muted small">· {i.scheduled_at ? dateTime(i.scheduled_at) : "date à fixer"}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <p className="small muted">
            {s.brouillons} bail(aux) en brouillon · {s.depots_attendus} dépôt(s) de garantie non intégralement reçu(s)
          </p>
        </div>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Onglet « Préparer le bail » de la candidature (page du module location)

type Draft = {
  application: { id: string; status: string; household_label: string };
  retenue: boolean;
  existing_lease: { id: string; status: string; status_label: string } | null;
  habitation: boolean;
  prefill: {
    campaign_title: string;
    property_label: string;
    property_address: string;
    regime: string;
    regime_label: string;
    rent_cents: number;
    charges_cents: number;
    charges_mode: string;
    deposit_cents: number;
    start_on: string;
    duration_months: number;
    parties: { role: string; full_name: string; email: string | null; account_linked: boolean }[];
  };
  deposit_problem: string | null;
  prorata: Prorata;
};

export function ApplicationLeaseTab({ orgId, applicationId }: { orgId: string; applicationId: string }) {
  const nav = useNavigate();
  const invalidate = useInvalidate();
  const q = useGet<Draft>(`/api/v1/orgs/${orgId}/leases/draft-from-application/${applicationId}`);
  const [form, setForm] = useState<{ start_on: string; duration_months: string; payment_day: string } | null>(null);
  const { busy, error, run } = useAction();
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const d = q.data;
  if (d.existing_lease) {
    return (
      <Card title="Bail">
        <p>
          Un bail existe pour ce dossier : <LeaseStatus status={d.existing_lease.status} />
        </p>
        <ButtonLink to={`/espace/${orgId}/baux/${d.existing_lease.id}`}>Ouvrir le bail</ButtonLink>
      </Card>
    );
  }
  if (!d.retenue) {
    return (
      <Notice title="Le bail se prépare pour un dossier retenu">
        Retenez d’abord ce dossier : le bail reprendra le logement, les conditions de la campagne et les personnes du dossier, avec leur source. Retenir un dossier ne lance ni signature ni encaissement.
      </Notice>
    );
  }
  if (!d.habitation) return <Notice tone="attention">Ce régime relève du module Professionnels.</Notice>;
  const f = form ?? { start_on: d.prefill.start_on, duration_months: String(d.prefill.duration_months), payment_day: "1" };
  const src = `Campagne « ${d.prefill.campaign_title} »`;
  return (
    <Card title="Préparer le bail">
      <div className="stack">
        <Notice tone="attention">{LEGAL_NOTICE}</Notice>
        <KeyValues
          items={[
            ["Logement", <>{d.prefill.property_label} <Source doc={src} /></>],
            ["Régime", <>{d.prefill.regime_label} <Source doc={src} /></>],
            ["Loyer hors charges", <><span className="entree-money">{money(d.prefill.rent_cents)}</span> <Source doc={src} /></>],
            [d.prefill.charges_mode === "forfait" ? "Charges (forfait)" : "Provision sur charges", <><span className="entree-money">{money(d.prefill.charges_cents)}</span> <Source doc={src} /></>],
            ["Dépôt de garantie", <><span className="entree-money">{money(d.prefill.deposit_cents)}</span> <Source doc={src} /></>],
            ["Personnes", d.prefill.parties.map((p) => `${p.full_name} (${p.role === "garant" ? "garant" : "locataire"})`).join(", ")],
          ]}
        />
        {d.deposit_problem ? <Notice tone="erreur">{d.deposit_problem} Corrigez le montant dans le bail avant de l’approuver.</Notice> : null}
        <ErrorBox error={error} />
        <div className="entree-grid-form">
          <Input label="Prise d’effet" type="date" value={f.start_on} error={fieldError(error, "start_on")} onChange={(e) => setForm({ ...f, start_on: e.target.value })} />
          <Input label="Durée (mois)" type="number" value={f.duration_months} error={fieldError(error, "duration_months")} onChange={(e) => setForm({ ...f, duration_months: e.target.value })} />
          <Input label="Jour de paiement" type="number" min={1} max={28} value={f.payment_day} error={fieldError(error, "payment_day")} onChange={(e) => setForm({ ...f, payment_day: e.target.value })} />
        </div>
        <p className="small muted">Premier loyer indicatif : {d.prorata.explanation}</p>
        <div>
          <Button
            busy={busy === "create"}
            onClick={async () => {
              let id = "";
              const ok = await run(
                "create",
                async () => {
                  const r = await post<{ lease: { id: string } }>(`/api/v1/orgs/${orgId}/leases/from-application`, {
                    application_id: applicationId,
                    start_on: f.start_on,
                    duration_months: Number(f.duration_months) || null,
                    payment_day: Number(f.payment_day) || 1,
                  });
                  id = r.lease.id;
                },
                "Bail préparé en brouillon.",
              );
              if (ok) {
                invalidate(`/api/v1/orgs/${orgId}/leases`);
                nav(`/espace/${orgId}/baux/${id}`);
              }
            }}
          >
            Préparer le bail
          </Button>
        </div>
      </div>
    </Card>
  );
}
